<template>
	<div
		v-if="items.length > 0"
		:data-chronicle="chronicId"
		data-e2e="brdcrmb"
		data-module-type="link-list"
	>
		<WebmdBreadcrumb
			class="breadcrumb"
			separator="/"
			v-metric="{ module: metrics.module }"
		>
			<WebmdBreadcrumbItem
				v-if="cancerCenters.includes(frndlyUrlData.id)"
			>
				<a
					:href="`${domain}/cancer/default.htm`"
					v-metric="{ link: 1 }"
				>
					Cancer
				</a>
			</WebmdBreadcrumbItem>
			<WebmdBreadcrumbItem
				v-for="(item, index) in items"
				:key="item.title"
			>
				<a
					:href="item.link"
					v-metric="{ link: getAdjustedLinkNumber(index) }"
				>
					{{ item.name }}
				</a>
			</WebmdBreadcrumbItem>
		</WebmdBreadcrumb>
		<script
			v-html="jsonldObj"
			type="application/ld+json"
		></script>
	</div>
</template>

<script>
import useStore from 'webmd-cms-store/src/store';

import _has from 'lodash/has';
import _pick from 'lodash/pick';
import { Breadcrumb, BreadcrumbItem } from 'webmd-elements';

export default {
	name: 'WebmdGlobalBreadcrumb',

	components: {
		WebmdBreadcrumb: Breadcrumb,
		WebmdBreadcrumbItem: BreadcrumbItem,
	},

	props: {
		chronicId: {
			type: String,
			required: true,
		},
		isDev: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			metrics: {
				module: 'brdcrmb',
			},
			items: [],
			moduleData: {},
			frndlyUrlData: {},
			domain: 'https://www.webmd.com',
			cancerCenters: ['1020', '1030', '1071', '1075', '1089', '1097', '1000520'],
		};
	},

	computed: {
		/*
		 * Return breadcrumb data in Array to the component template
		 */
		getItems() {
			const module = this.moduleData;
			let breadData = [];

			/*
			 * Current data is returned unconsistently as Array (multiple) or Object (single)
			 * if module_data is an Object, we will wrap in an Array to make consistent
			 */
			if (module
				&& _has(module, 'module_data.breadcrumb.bread')
				&& module.module_data.breadcrumb.bread) {
				if (Array.isArray(module.module_data.breadcrumb.bread) !== true) {
					// wrap Array around bread object
					breadData = [module.module_data.breadcrumb.bread];
				} else {
					breadData = module.module_data.breadcrumb.bread;
				}
			}

			const itemDetails = Array.isArray(breadData) && breadData.length
				? breadData.map((crumb) => ({
					name: crumb.piece,
					prefix: crumb.prefix,
					link: this.getCrumbUrl(crumb.friendlyurl, this.pUrl),
				}))
				: breadData;

			return itemDetails.map((item) => {
				if (item.name === 'Reference' && this.isPetSeeker) {
					return { ...item, name: 'Pet Meds', link: `https://www.webmd.com/pets/pet-meds` };
				}
				return item;
			});
		},


		jsonldObj() {
			const ldObj = {
				'@context': 'https://schema.org',
				'@type': 'BreadcrumbList',
				itemListElement: [],
			};
			const ldArray = this.items;
			const itemArray = ldArray.map((obj, index) => {
				const newObj = {
					'@type': 'ListItem',
					position: index + 1,
					name: obj.name,
					item: obj.link,
				};

				return newObj;
			});

			ldObj.itemListElement = itemArray;
			return JSON.stringify(ldObj);
		},

		getPageUrl() {
			if (!this.pageData) {
				return [];
			}
			const pagedata = this.pageData;
			if (_has(pagedata, 'meta.og_tags.url')) {
				return pagedata.meta.og_tags.url;
			}
			return '';
		},

		/*
		 * Filter data based on chronocle id for breadcrumb
		 */
		getModuleByChronicId() {
			let module = this.store.cms.getModules(this.chronicId);

			if (_has(module, 'module_data.breadcrumb.bread')) {
				module = _pick(module, ['module_data.breadcrumb.bread']);

				let breadCrumbs = module.module_data.breadcrumb.bread;

				if (Array.isArray(breadCrumbs) !== true) {
					breadCrumbs = [breadCrumbs];
				}

				module.module_data.breadcrumb.bread = breadCrumbs.map((obj) => _pick(obj, ['friendlyurl', 'piece', 'prefix']));

				this.store.cms.UPDATE_STATE({ key: this.chronicId, value: module });
			}

			return module;
		},
		getPageData() {
			return this.store.pagedata;
		},

		getFriendlyUrlData() {
			const friendlyUrlData = this.store.cms.friendlyURLData;
			// eslint-disable-next-line max-len
			if (friendlyUrlData && friendlyUrlData.target && friendlyUrlData.target.centers && friendlyUrlData.target.centers.Health) {
				return friendlyUrlData.target.centers.Health;
			}

			return {};
		},
		isPetSeeker() {
			return this.store.pagedata.omniture.s_publication_source === 'WebMD Pet Seeker Drug Reference';
		},
	},

	created() {
		this.store = useStore(this.$pinia);
		this.moduleData = this.getModuleByChronicId;
		this.pageData = this.getPageData;
		this.pUrl = this.getPageUrl;
		this.frndlyUrlData = this.getFriendlyUrlData;
		this.items = this.getItems;

		// this.$store.commit('cms/SET_RENDERED', this.chronicId);
	},
	methods: {
		getAdjustedLinkNumber(index) {
			return this.cancerCenters.includes(this.frndlyUrlData.id) ? index + 2 : index + 1;
		},
		getCrumbUrl(furl, pUrl) {
			const urlWithEnv = pUrl.split('/').reverse().slice(1).reverse()
				.join('/');
			const pageDomainurl = `${pUrl.split('.com/')[0]}.com`;
			let breadcrumbUrl = '';

			if (furl === '/fs-guide-breadcrumb') {
				breadcrumbUrl = `${urlWithEnv.replace('/cm', '')}/guide-toc`;
			} else if (furl === '/fs-quiz-breadcrumb') {
				breadcrumbUrl = `${urlWithEnv.replace('/cm', '')}/quiz/default.htm`;
			} else if (furl === '/fs-medref-breadcrumb') {
				breadcrumbUrl = `${urlWithEnv.replace('/cm', '')}/medical-reference/default.htm`;
			} else if (furl.includes('.com') && !furl.includes('//')) {
				breadcrumbUrl = `//${furl}`;
			} else {
				breadcrumbUrl = pageDomainurl + furl;
			}
			return breadcrumbUrl;
		},
	},
	mounted() {
		if (window.location.origin) {
			this.domain = window.location.origin;
		}
	},

};
</script>
<style lang="scss" src="./stylesheets/breadcrumb.scss">
</style>
